import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectAccessToken, type WithAuthState } from '../auth/selectors';
import { resolvePhotoObjectUrl } from './photoObjectUrl';

export type PhotoObjectUrlState =
  | { readonly status: 'none' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly url: string }
  | { readonly status: 'error' };

/**
 * A presigned photo as an object URL an `<img src>` can use. The networking
 * rule it obeys — and why it is two hops rather than one redirect — is in
 * `photoObjectUrl.ts`; this is only the React binding.
 *
 * `path === null` answers `{ status: 'none' }` synchronously with **no
 * fetch**: most rows on a page of 25 have no photo at all, and a roster must
 * not fire N requests for photo-less people.
 *
 * `version` is whatever changes when the photo does (`photoUpdatedAt`), and is
 * the cache key: a retake re-runs the fetch and revokes the old blob. An
 * immutable photo (a task photo) passes nothing.
 *
 * The object URL is owned by the module cache, not by this component: it is
 * never revoked on unmount, because the next mount — or the dialog opening
 * over the row — is entitled to the same blob.
 */
export function usePhotoObjectUrl(
  path: string | null,
  version: string | null = null,
): PhotoObjectUrlState {
  const token = useSelector<WithAuthState, string | null>(selectAccessToken);
  const [state, setState] = useState<PhotoObjectUrlState>(
    path === null ? { status: 'none' } : { status: 'loading' },
  );

  useEffect(() => {
    if (path === null) {
      setState({ status: 'none' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    resolvePhotoObjectUrl(path, version, token)
      .then((url) => {
        if (!cancelled) setState({ status: 'ready', url });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [path, version, token]);

  return state;
}

/**
 * A person's avatar: `me` for the caller's own (the only one a worker may
 * ever ask for — P-11 expressed as an API surface rather than as a check
 * somebody could forget), any user id for the admin roster.
 */
export function useUserPhotoObjectUrl(
  userId: string | 'me',
  photoUpdatedAt: string | null,
): PhotoObjectUrlState {
  const path = userId === 'me' ? '/me/photo' : `/users/${userId}/photo`;
  return usePhotoObjectUrl(photoUpdatedAt === null ? null : path, photoUpdatedAt);
}
